import type {
    DayStep,
    EngineeringSystem,
    HeroView,
    NavLink,
    PlannerRequest,
    SpecGroup
} from '@/types'

export const HERO_IMAGE_RATIO = 2752 / 1536

export const HERO_DURATION_MS = 8000

export const NAV_LINKS: Array<NavLink> = [
    { label: 'Home life', href: '#everyday' },
    { label: 'Engineering', href: '#engineering' },
    { label: 'Intelligence', href: '#intelligence' },
    { label: 'Safety', href: '#safety' },
    { label: 'Specs', href: '#specs' }
]

export const HERO_VIEWS: Array<HeroView> = [
    {
        id: 'presence',
        index: '01',
        label: 'Presence',
        caption: "165 cm of calm, at home in your living room.",
        src: '/images/hero-presence.jpg',
        alt: "Oriel, a slender humanoid robot in an oatmeal knit body and off-white ceramic shell, standing in a sunlit living room holding a folded linen throw.",
        subjectX: 0.52,
        subjectY: 0.45,
        targetX: 0.5,
        targetXWide: 0.66,
        readouts: [
            { label: 'Height', value: 165, unit: 'cm' },
            { label: 'Weight', value: 32, unit: 'kg' },
            { label: 'Runtime', value: 4, unit: 'h' }
        ],
        annotations: [
            { x: 50.7, y: 14.3, title: 'Perception', detail: "Stereo cameras and depth, behind smoked glass", side: 'right' },
            { x: 59, y: 44, title: 'Hands', detail: "16 degrees of freedom in each", side: 'right' },
            { x: 52.5, y: 71.5, title: 'Balance', detail: "Whole-body control, 1,000 times a second", side: 'right' }
        ]
    },
    {
        id: 'motion',
        index: '02',
        label: 'Motion',
        caption: "Walks heel to toe, at the pace of a person.",
        src: '/images/hero-motion.jpg',
        alt: "Oriel walking mid-stride down a sunlit hallway, carrying a woven basket of folded white towels against its hip.",
        subjectX: 0.54,
        subjectY: 0.5,
        targetX: 0.5,
        targetXWide: 0.64,
        readouts: [
            { label: 'Walking pace', value: 1.1, decimals: 1, unit: 'm/s' },
            { label: 'Stride', value: 0.52, decimals: 2, unit: 'm' },
            { label: 'Carry', value: 12, unit: 'kg' }
        ],
        annotations: [
            { x: 50.5, y: 15, title: 'Wayfinding', detail: "Maps each room as it moves through it", side: 'right' },
            { x: 58.5, y: 46.5, title: 'Carry', detail: "Up to 12 kg, held level", side: 'right' },
            { x: 49.5, y: 69, title: 'Gait', detail: "Soft heel strike, quiet on oak", side: 'right' }
        ]
    },
    {
        id: 'vision',
        index: '03',
        label: 'Vision',
        caption: "Sees what it is holding, and what it is for.",
        src: '/images/hero-vision.jpg',
        alt: "Close-up of Oriel's rounded ceramic head, amber lights glowing behind its smoked-glass visor, looking down at a speckled ceramic mug in its hand.",
        subjectX: 0.62,
        subjectY: 0.45,
        targetX: 0.5,
        targetXWide: 0.66,
        readouts: [
            { label: 'Depth range', value: 6, unit: 'm' },
            { label: 'Objects in view', value: 43, unit: '' },
            { label: 'Confidence', value: 0.97, decimals: 2, unit: '' }
        ],
        annotations: [
            { x: 64.5, y: 32.5, title: 'Stereo vision', detail: "Two cameras, depth from 0.2 to 6 m", side: 'left' },
            { x: 76.5, y: 21, title: 'Listening', detail: "Six microphones, processed on board", side: 'right' }
        ]
    },
    {
        id: 'hands',
        index: '04',
        label: 'Hands',
        caption: "Gentle enough for crystal, steady enough for a full pot.",
        src: '/images/hero-hands.jpg',
        alt: "Macro view of Oriel's articulated graphite fingers lifting a thin-stemmed wine glass from an oak table by candlelight.",
        subjectX: 0.6,
        subjectY: 0.5,
        targetX: 0.5,
        targetXWide: 0.64,
        readouts: [
            { label: 'Grip force', value: 1.2, decimals: 1, unit: 'N' },
            { label: 'Hand joints', value: 16, unit: 'DOF' },
            { label: 'Touch', value: 0.1, decimals: 1, unit: 'N' }
        ],
        annotations: [
            { x: 58, y: 40.5, title: 'Finger joints', detail: "16 actuated degrees of freedom", side: 'left' },
            { x: 57.5, y: 66, title: 'Tactile fingertips', detail: "Feels as little as 0.1 N", side: 'right' },
            { x: 80, y: 43, title: 'Wrist', detail: "Gives way when bumped", side: 'left' }
        ]
    }
]

export const PRESENCE_STATS = [
    { value: '165', unit: 'cm', label: 'About your height, so it reaches what you reach' },
    { value: '32', unit: 'kg', label: 'Light enough for upstairs floors and rugs' },
    { value: '4', unit: 'h', label: 'Of work per charge, then it docks itself' },
    { value: '< 45', unit: 'dB', label: 'Quieter than your dishwasher while it moves' }
]

export const DAY_STEPS: Array<DayStep> = [
    {
        id: 'morning',
        time: '07:30',
        room: 'Living room',
        title: 'Waters the plants.',
        body: "Follows the watering schedule you set, pot by pot, and pours slowly so nothing spills over.",
        note: "Lets you know when a plant looks dry between waterings.",
        src: '/images/day-plants.jpg',
        alt: "Oriel watering potted herbs and a fiddle-leaf fig on a sunny windowsill with a small brass watering can.",
        stop: 0
    },
    {
        id: 'midday',
        time: '11:00',
        room: 'Bedroom',
        title: 'Folds the laundry.',
        body: "Towels, tees and trousers, folded and stacked the way you showed it once.",
        note: "Anything marked delicate is set aside on the chair for you.",
        src: '/images/day-laundry.jpg',
        alt: "Oriel folding a white bath towel at the foot of a neatly made bed beside stacks of folded laundry.",
        stop: 1
    },
    {
        id: 'evening',
        time: '19:45',
        room: 'Kitchen',
        title: 'Loads the dishwasher.',
        body: "Clears the table, scrapes plates, and loads glasses one at a time.",
        note: "Sharp knives and hand-wash pieces stay by the sink for you.",
        src: '/images/day-kitchen.jpg',
        alt: "Oriel bending at the knees to place a white plate into an open dishwasher in a warmly lit kitchen at dusk.",
        stop: 2
    },
    {
        id: 'night',
        time: '21:30',
        room: 'Living room',
        title: 'Puts the day away.',
        body: "Toys back in their basket, cushions squared, lights low.",
        note: "Then back to its dock in the hall to charge overnight.",
        src: '/images/day-tidy.jpg',
        alt: "Oriel kneeling on a wool rug at night, placing colourful wooden toys into a woven basket beside a glowing lamp.",
        stop: 3
    }
]

export const ENGINEERING_SYSTEMS: Array<EngineeringSystem> = [
    {
        id: 'perception',
        title: 'Perception',
        summary: "Stereo cameras, a depth sensor and six microphones sit behind the visor. Everything is processed on board.",
        specs: ['2 × stereo cameras', 'Depth 0.2 to 6 m', '6-mic array'],
        x: 46.2,
        y: 8.3
    },
    {
        id: 'actuation',
        title: 'Actuation',
        summary: "Custom low-gear motors in every joint. Strong enough to lift, and backdrivable, so a bump makes it yield instead of push.",
        specs: ['30 body joints', 'Torque sensing', 'Backdrivable'],
        x: 61,
        y: 25
    },
    {
        id: 'power',
        title: 'Power',
        summary: "A swappable battery sits low in the torso. When it runs low, Oriel finishes the task in hand and walks itself to its dock.",
        specs: ['4 h typical use', '80% in 60 min', 'Self-docking'],
        x: 49.6,
        y: 36
    },
    {
        id: 'dexterity',
        title: 'Dexterity',
        summary: "Sixteen degrees of freedom in each hand, with tactile pads in every fingertip that sense how firmly it is holding.",
        specs: ['16 DOF per hand', 'Tactile fingertips', 'Wrist cameras'],
        x: 64.3,
        y: 55
    },
    {
        id: 'shell',
        title: 'Soft exterior',
        summary: "A padded knit layer over the frame means no pinch points and nothing hard at shoulder height. The covers come off and wash.",
        specs: ['Machine-washable knit', 'No pinch points', 'Oat, Slate, Clay'],
        x: 42.9,
        y: 57.5
    },
    {
        id: 'balance',
        title: 'Balance',
        summary: "Whole-body control adjusts a thousand times a second, so it steadies itself on stairs, thresholds and thick rugs.",
        specs: ['1 kHz control loop', 'Stairs to 20 cm rise', '1.1 m/s indoors'],
        x: 58.3,
        y: 69.5
    }
]

export const PLANNER_REQUESTS: Array<PlannerRequest> = [
    {
        id: 'glasses',
        request: 'Bring me my reading glasses',
        context: ['Last seen on the side table, living room', 'You are in the kitchen'],
        steps: [
            'Walks to the living room',
            'Picks the glasses up by the frame, never the lenses',
            'Carries them through to the kitchen',
            'Hands them over at chest height, and waits'
        ],
        question: "There are two pairs on the table. The tortoiseshell ones?",
        duration: '41 s'
    },
    {
        id: 'tidy',
        request: 'Tidy the living room',
        context: ['14 things out of place', '2 things it has not seen before'],
        steps: [
            'Returns cushions and throws to the sofa',
            'Puts books back on the shelf, spines out',
            'Sorts toys into the basket by the window',
            'Leaves anything unfamiliar on the side table'
        ],
        question: "I found a blue folder on the floor. Where should it live?",
        duration: '9 min'
    },
    {
        id: 'dishes',
        request: 'Unload the dishwasher',
        context: ['Cycle finished at 21:40', 'Plates, bowls, 9 glasses, cutlery'],
        steps: [
            'Checks everything is dry and cool',
            'Stacks plates and bowls in the upper cabinet',
            'Places glasses one at a time, rims up',
            'Leaves sharp knives in the rack for you'
        ],
        question: "The large pan is still wet. Leave it on the rack?",
        duration: '7 min'
    },
    {
        id: 'laundry',
        request: 'Take the laundry upstairs',
        context: ['Basket in the hall, about 6 kg', 'Bedroom on the first floor'],
        steps: [
            'Lifts the basket with both hands',
            'Takes the stairs one step at a time, holding the rail',
            'Sets the basket at the foot of the bed',
            'Folds the towels, puts delicates aside'
        ],
        question: "Should I fold the shirts or hang them?",
        duration: '6 min'
    }
]

export const CAN_DO = [
    'Tidies rooms and returns things to where they live',
    'Loads and unloads the dishwasher',
    'Folds towels, tees and trousers',
    'Carries laundry and shopping between floors',
    'Waters plants on your schedule',
    'Fetches things you ask for, and hands them over',
    'Opens doors, takes stairs, docks itself to charge'
]

export const NOT_YET = [
    'Cooking at the hob or oven',
    'Using knives or other sharp tools',
    'Lifting, bathing or dressing a person',
    'Climbing ladders or working at height',
    'Looking after children or pets on its own',
    'Leaving the house'
]

export const SPEC_GROUPS: Array<SpecGroup> = [
    {
        title: 'Body',
        rows: [
            { label: 'Height', value: '165 cm' },
            { label: 'Weight', value: '32 kg' },
            { label: 'Carry', value: 'Up to 12 kg' },
            { label: 'Exterior', value: 'Washable knit over padded panels' }
        ]
    },
    {
        title: 'Movement',
        rows: [
            { label: 'Joints', value: '30 in the body' },
            { label: 'Walking pace', value: 'Up to 1.1 m/s indoors' },
            { label: 'Stairs', value: 'Up to 20 cm per step' },
            { label: 'Noise', value: 'Under 45 dB while walking' }
        ]
    },
    {
        title: 'Hands',
        rows: [
            { label: 'Degrees of freedom', value: '16 per hand' },
            { label: 'Touch', value: 'Tactile pad in every fingertip' },
            { label: 'Grip', value: '0.1 to 60 N' },
            { label: 'Cameras', value: 'One in each wrist' }
        ]
    },
    {
        title: 'Perception',
        rows: [
            { label: 'Vision', value: 'Stereo cameras with depth' },
            { label: 'Range', value: '0.2 to 6 m' },
            { label: 'Hearing', value: 'Six-microphone array' },
            { label: 'Privacy', value: 'Physical camera shutter' }
        ]
    },
    {
        title: 'Power',
        rows: [
            { label: 'Runtime', value: 'About 4 h of typical use' },
            { label: 'Charging', value: '80% in 60 min' },
            { label: 'Dock', value: 'Finds and returns on its own' },
            { label: 'Battery', value: 'Swappable, in the torso' }
        ]
    },
    {
        title: 'Intelligence',
        rows: [
            { label: 'Compute', value: 'On board, works offline' },
            { label: 'Voice', value: 'Speaks and listens in English' },
            { label: 'Updates', value: 'New skills every month' },
            { label: 'App', value: 'iOS and Android' }
        ]
    }
]

export const FINISHES = [
    { name: 'Oat', swatch: 'bg-[oklch(0.84_0.035_78)]' },
    { name: 'Slate', swatch: 'bg-[oklch(0.46_0.012_250)]' },
    { name: 'Clay', swatch: 'bg-[oklch(0.62_0.08_45)]' }
]
